import { FormEvent, useContext, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { ModalEstadoContext } from '../../contexts/ModalEstado'
import { criptografarDado, descriptografarDado } from '../../helpers'
import { paramsGrupoLista } from "../../interfaces/grupos"
import { usuariosRespostaPayload } from '../../interfaces/usuarios'
import Permissao from '../../middleware/Permissoes'
import { atualizarGrupo, novoGrupo } from "../../services/grupos"
import { atualizarUsuario, listarUsuariosOrganizacao } from '../../services/usuarios'
import { Button } from '../Styles'

function ModalGrupo({ grupoSelecionado, onClose }: paramsGrupoLista) {
  const { atualizarDados, modalExibir, atualizarInformacoes, modalSecundarioExibir } = useContext(ModalEstadoContext)

  const [processando, setProcessando] = useState(false)

  const [titulo, setTitulo] = useState<string>(grupoSelecionado?.titulo || "");
  const [descricao, setDescricao] = useState<string>(grupoSelecionado?.descricao || "");
  const [situacao, setSituacao] = useState(grupoSelecionado?.situacao);

  const [usuarios, setUsuarios] = useState<usuariosRespostaPayload[]>([]);
  const [usuariosVinculados, setUsuariosVinculados] = useState<usuariosRespostaPayload[]>([]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    const permissoesArray = JSON.parse(descriptografarDado(localStorage.getItem('permissao') ?? ""))
    const permissoesAutorizadas = ['master', 'administrativo', 'configuracoes_grupos_permissoes']
    const temPermissao = permissoesArray.some((permissao: string) => permissoesAutorizadas.includes(permissao))

    if (!temPermissao) return toast.error("Vocë não tem permissão para cadastar um novo grupo de permissões")

    setProcessando(true)

    try {
      const payload = {
        titulo,
        descricao
      }
      const resposta = await novoGrupo(payload)

      if (resposta.status === 201) {
        setProcessando(false)
        atualizarDados()
        modalExibir("")
        return toast.success("Grupo Cadastrado com Sucesso.")
      }
    } catch {
      toast.error("Erro ao Cadastrar Grupo")
    }
  }

  const handleUpdate = async (e: FormEvent) => {
    e.preventDefault()

    const permissoesArray = JSON.parse(descriptografarDado(localStorage.getItem('permissao') ?? ""))
    const permissoesAutorizadas = ['master', 'configuracao_grupos_permissao']
    const temPermissao = permissoesArray.some((permissao: string) => permissoesAutorizadas.includes(permissao))

    if (!temPermissao) return toast.error("Vocë não tem permissão para atualizar dados de um grupo de permissões")

    setProcessando(true)

    if (!grupoSelecionado) return toast.error("Grupo não encontrado.")
    const idGrupoCript = criptografarDado(grupoSelecionado.id.toString())
    try {
      const payload = {
        titulo,
        descricao,
        situacao,
      }

      const resposta = await atualizarGrupo(idGrupoCript, payload)

      if (resposta.status === 200) {
        setProcessando(false)
        atualizarDados()
        modalExibir("")
        return toast.success("Grupo Atualizado com Sucesso.")
      }
    } catch (error: any) {
      if (error.response.status === 404) {
        setProcessando(false)
        modalExibir("")
        return toast.warn("Grupo não encontrado.")
      }
      if (error.response.status === 409) {
        setProcessando(false)
        modalExibir("")
        modalSecundarioExibir("ModalTransferenciaGrupo")
        return toast.warn("É necessário Migrar os Usuarios para outro grupo antes de Inativar o Grupo.")
      }
      return toast.error("Erro ao Atualizar Grupo")
    }
  }

  const adicionarUsuarioNoGrupo = async (idUsuario: string) => {
    try {
      if (!grupoSelecionado) return
      const permissoesArray = JSON.parse(descriptografarDado(localStorage.getItem('permissao') ?? ""))
      const permissoesAutorizadas = ['master', 'administrativo', 'configuracao_grupos_permissao']
      const temPermissao = permissoesArray.some((permissao: string) => permissoesAutorizadas.includes(permissao))

      if(!temPermissao) return toast.warn("Você não tem permissão para adicionar usuarios no grupo.")
      const payload = {
        idGrupoPermissao: grupoSelecionado.id
      };

      const resposta = await atualizarUsuario(idUsuario, payload);
      if (resposta.status === 200) {
        atualizarDados()
        toast.success("Usuário vinculado com sucesso.");
      }
    } catch {
      toast.error("Erro ao vincular usuário.");
    }
  };

  const removerUsuarioNoGrupo = async (idUsuario: string) => {
    try {
      if (!grupoSelecionado) return
      const permissoesArray = JSON.parse(descriptografarDado(localStorage.getItem('permissao') ?? ""))
      const permissoesAutorizadas = ['master', 'administrativo', 'configuracao_grupos_permissao']
      const temPermissao = permissoesArray.some((permissao: string) => permissoesAutorizadas.includes(permissao))

      if(!temPermissao) return toast.warn("Você não tem permissão para remover usuarios do Grupo.")
      const payload = {
        idGrupoPermissao: null
      };

      const resposta = await atualizarUsuario(idUsuario, payload);
      if (resposta.status === 200) {
        atualizarDados()
        toast.success("Usuário desvinculado com sucesso.");
      }
    } catch {
      toast.error("Erro ao desvincular usuário.");
    }
  };

  useEffect(() => {
    const listarTodosUsuarios = async () => {
      if (!grupoSelecionado) {
        setUsuarios([]);
        return;
      }

      try {
        const resposta = await listarUsuariosOrganizacao({ termo: "", situacao: "ativo" });
        setUsuarios(resposta.data ?? []);
      } catch {
        toast.error("Erro ao listar os usuários.");
      }
    }

    listarTodosUsuarios()
  }, [grupoSelecionado, atualizarInformacoes]);

  useEffect(() => {
    const listarUsuariosVinculadosGrupo = async () => {
      if (!grupoSelecionado) {
        setUsuarios([]);
        return;
      }

      try {
        const params = {
          idGrupo: grupoSelecionado.id.toString()
        }
        const resposta = await listarUsuariosOrganizacao(params)

        setUsuariosVinculados(resposta.data ?? [])
      } catch {
        toast.error("Erro ao carregar os usuários vinculados.")
      }
    }

    listarUsuariosVinculadosGrupo()

  }, [grupoSelecionado, atualizarInformacoes])

  return (
    <div id='fundoModal'>
      <section className='containerModalCentro'>
        <span id='novoContainerTitulo'>
          <p>{grupoSelecionado ? "Atualizar Grupo" : "Cadastrar Grupo"}</p>
        </span>
        <form id='formModalNovo' onSubmit={grupoSelecionado ? handleUpdate : handleSubmit}>
          <section>
            <span>
              <label>Titulo do Grupo</label>
              <input type="text" placeholder="Titulo do Grupo" onChange={(e) => setTitulo(e.target.value)} required={!grupoSelecionado} defaultValue={titulo} maxLength={100} />
            </span>
          </section>

          <section>
            <span>
              <label>Descrição do Grupo</label>
              <textarea
                className="textAreaModal"
                placeholder="Descrição do Grupo"
                defaultValue={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                maxLength={500}
                required={!grupoSelecionado}>

              </textarea>
            </span>
          </section>
          {grupoSelecionado &&
            <section>
              <span>
                <label>Situação</label>
                <select onChange={(e) => setSituacao(e.target.value)} required={!!grupoSelecionado} defaultValue={situacao}>
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                </select>
              </span>
            </section>
          }
          {grupoSelecionado && (
            <Permissao permissoes={['master', 'configuracoes_grupos_usuarios']}>
              <details className="collapse">
                <summary className="collapseTitle">Gerenciar Usuários</summary>

                <div className="usuariosContainer">
                  <section>
                    <select defaultValue=""
                      onChange={(e) => adicionarUsuarioNoGrupo(e.target.value)}
                    >
                      <option value="">Escolha um usuário</option>
                      {usuarios.filter((u) => u.idGrupoPermissao === null).map((usuario: usuariosRespostaPayload) => (
                        <option value={usuario.id.toString()} key={usuario.id}>{usuario.nome}</option>
                      ))}
                    </select>
                  </section>
                </div>

                <div className="usuariosList">
                  {usuariosVinculados.length === 0 ? (
                    <p className="emptyText">Nenhum usuário vinculado ainda.</p>
                  ) : (
                    usuariosVinculados.map((usuarioVinculado: usuariosRespostaPayload) => (
                      <div
                        key={usuarioVinculado.id}
                        className="usuarioItem"
                        onClick={() => removerUsuarioNoGrupo(usuarioVinculado.id)}
                      >
                        <span>{usuarioVinculado.nome}</span>
                      </div>
                    ))
                  )}
                </div>
              </details>
            </Permissao>
          )}

          <span id="botoesContainer">
            <Button type='button' cor='cancelar' isLoading={processando} onClick={onClose}>Cancelar</Button>
            <Button type='submit' cor='salvar' isLoading={processando} disabled={processando}>Salvar Dados</Button>
          </span>
        </form>
      </section>
    </div>
  );
}

export default ModalGrupo;